import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { useBusinessServiceRequest } from '@ingenico/react-section-utils';
import { DATAGRID_DEFAULT_PAGE_SIZE } from '@ingenico/react-ui/datagrid';
import type { OnTriggerProps, UseDatagridQueryTriggerProps } from '@ingenico/react-ui/hooks/use-datagrid-query-trigger';
import { useDatagridQueryTrigger } from '@ingenico/react-ui/hooks/use-datagrid-query-trigger';
import type { EmptyListProps } from '@ingenico/react-ui/notice-card';
import type { GridApiPremium } from '@mui/x-data-grid-premium';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import type { GridSortModel, GridValidRowModel } from '@mui/x-data-grid-pro';
import { toArray } from 'lodash-es';
import type { MutableRefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DeviceManagementService } from '../../domain/business/services/deviceManagement.service';
import { useCancelDeviceLogFile } from '../../hooks/useCancelDeviceLogFile.hook';
import { useDeviceLogsModalDatagridColumns } from '../../hooks/useDeviceLogsModalDatagridColumns.hook';
import { useManageDeviceLogsModalActions } from '../../hooks/useManageDeviceLogsModalActions.hook';
import type { DeviceLogsResponse } from '../../model/deviceLogs.model';
import type {
  DeviceLogsGridAttributes,
  UseDeviceLogsModalProps,
  UseDeviceLogsModalReturn,
} from './DeviceLogsModal.model';
import {
  defaultDeviceLogsGridSortModel,
  getDeviceLogsFilters,
  transformDeviceLogsGridSort,
} from './DeviceLogsModal.util';
import { useDeviceManagementCapability } from '../../hooks';

export function useDeviceLogsModalVM({
  logicalDeviceId,
  isOpen,
  connectionStatus,
  refetchIsInProgress,
}: UseDeviceLogsModalProps): UseDeviceLogsModalReturn {
  const datagridRef = useGridApiRef();

  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(DATAGRID_DEFAULT_PAGE_SIZE);
  const [sort, setSort] = useState<GridSortModel>(defaultDeviceLogsGridSortModel);
  const [_page, setPage] = useState(0);

  // ==========================================
  // ============= request call ===============

  const body = useMemo(() => ({ filters: getDeviceLogsFilters(), sort: transformDeviceLogsGridSort(sort) }), [sort]);

  const params = useMemo(() => ({ offset, limit }), [limit, offset]);

  const query = useMemo(() => ({ body, params }), [body, params]);

  const searchDeviceLogsQuery = useBusinessServiceRequest(
    DeviceManagementService,
    useCallback(
      (service, isRefetch) =>
        logicalDeviceId.length > 0 && isOpen
          ? service.searchDeviceLogs({ logicalDeviceId, isRefetch, ...query })
          : null,
      [logicalDeviceId, isOpen, query],
    ),
  );

  const {
    data: apiData,
    isLoading,
    isError,
    isSuccess,
  } = useMemo(() => searchDeviceLogsQuery, [searchDeviceLogsQuery]);

  // ==========================================
  // ========== manage first loading ==========

  const [isFirstLoading, setIsFirstLoading] = useState<boolean>(true);
  const isInitialized = useRef<boolean>(false);

  useEffect(() => {
    if (!isLoading && (isError || isSuccess) && !isInitialized.current) {
      isInitialized.current = true;
      setIsFirstLoading(false);
    }
  }, [isError, isLoading, isSuccess]);

  const resetTableState = useCallback(() => {
    setOffset(0);
    setLimit(DATAGRID_DEFAULT_PAGE_SIZE);
    setSort(defaultDeviceLogsGridSortModel);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      isInitialized.current = false;
      setIsFirstLoading(true);
      resetTableState();
    }
  }, [isOpen, resetTableState]);

  // ==========================================
  // =============== pagination ===============

  const total = useMemo<number>(() => {
    return apiData?.pagination?.totalResults ?? 0;
  }, [apiData?.pagination?.totalResults]);

  const onTrigger = useCallback(({ page: pageCallback, pageSize, sort: newSort }: OnTriggerProps) => {
    setOffset(pageCallback * pageSize);
    setLimit(pageSize);
    setSort(newSort);
    setPage(pageCallback);
  }, []);

  const serverData = useMemo<UseDatagridQueryTriggerProps['serverData']>(() => {
    return {
      offset,
      limit,
      total,
      sort,
    };
  }, [limit, offset, sort, total]);

  const datagridTriggers = useDatagridQueryTrigger({
    datagridRef: datagridRef as MutableRefObject<GridApiPremium>,
    onTrigger,
    serverData,
    filters: {},
  });

  // ==========================================
  // =============== datagrid =================

  const refetch = useCallback(() => {
    searchDeviceLogsQuery.refetch();
    refetchIsInProgress();
  }, [refetchIsInProgress, searchDeviceLogsQuery]);

  const { handleStopRecording, stopRecordingModal } = useManageDeviceLogsModalActions({ refetch });

  const hasLogcatWritePermission = useDeviceManagementCapability('logicalDevice.logcat.edit');

  const { cancelTrigger, cancelModal } = useCancelDeviceLogFile({ refetch });

  const { columns } = useDeviceLogsModalDatagridColumns({
    logicalDeviceId,
    handleCancelRequest: cancelTrigger,
    handleStopRecording,
    hasLogcatWritePermission,
    connectionStatus,
  });

  const getRowId = useCallback((row: GridValidRowModel) => (row as DeviceLogsResponse['data'][number]).id, []);

  const gridProps = useMemo<DeviceLogsGridAttributes>(
    () => ({
      ref: datagridRef as MutableRefObject<GridApiPremium>,
      total,
      handlePaginationModelChange: datagridTriggers.onPaginationModelChange,
      handleSortModelChange: datagridTriggers.onSortModelChange,
      sortModel: sort,
      pageSize: limit,
      loading: isLoading,
      rows: toArray(apiData?.data),
      rowCount: apiData?.pagination?.totalResults ?? 0,
      getRowId,
    }),
    [
      apiData?.data,
      apiData?.pagination?.totalResults,
      datagridRef,
      datagridTriggers.onPaginationModelChange,
      datagridTriggers.onSortModelChange,
      getRowId,
      isLoading,
      limit,
      sort,
      total,
    ],
  );

  const noRowsProps = useMemo<EmptyListProps>(
    () => ({
      image: 'empty-packages',
      isBordered: false,
      messageLabel: 'emptyListOrPage.message',
      namespace: ALL_LNGS_NAMESPACES.COMMON,
      titleLabel: 'emptyListOrPage.title',
    }),
    [],
  );

  // ==========================================

  return { columns, gridProps, isFirstLoading, noRowsProps, isError, stopRecordingModal, cancelModal };
}
